import type { MediaScope } from './types';
import { mediaApi } from '../api/media-api';

export interface UploadProgress {
  loaded: number;
  total: number;
}

export interface UploadOptions {
  scope: MediaScope;
  file: File;
  onProgress?: (progress: UploadProgress) => void;
  signal?: AbortSignal;
}

export interface UploadResult {
  mediaId: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
}

function putWithProgress(
  url: string,
  file: File,
  onProgress?: (p: UploadProgress) => void,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

    if (onProgress) {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) onProgress({ loaded: e.loaded, total: e.total });
      });
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
    };
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.onabort = () => reject(new DOMException('Upload aborted', 'AbortError'));

    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        return;
      }
      signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }

    xhr.send(file);
  });
}

export async function uploadMedia({
  scope,
  file,
  onProgress,
  signal,
}: UploadOptions): Promise<UploadResult> {
  const presign = await mediaApi.createUpload({
    scope,
    contentType: file.type || 'application/octet-stream',
    sizeBytes: file.size,
    fileName: file.name,
  });

  await putWithProgress(presign.uploadUrl, file, onProgress, signal);
  await mediaApi.confirmUpload(presign.mediaId);

  return {
    mediaId: presign.mediaId,
    fileName: file.name,
    contentType: file.type,
    sizeBytes: file.size,
  };
}
